import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { projectsAPI } from '../../services/api';
import { useAuth } from '../../contexts/AuthContext';
import { errorHandler } from '../../utils/errorHandler';
import { projectsCache } from '../../utils/cache';
import LoadingSpinner from '../../components/common/LoadingSpinner/LoadingSpinner';
import ErrorDisplay from '../../components/common/ErrorDisplay/ErrorDisplay';
import { getStorage, ref as storageRef, uploadBytes, getDownloadURL } from 'firebase/storage';
import { STORAGE_PATHS } from '../../config/constants';
import { v4 as uuidv4 } from 'uuid';
import './Projects.css';
import '../../styles/shared/PageHeader.css';
import { isAdmin, isSuperAdmin } from '../../utils/auth-utils';

function Projects() {
    const [projects, setProjects] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [hasApplied, setHasApplied] = useState(false);
    const [showAddForm, setShowAddForm] = useState(false);
    const [editingProject, setEditingProject] = useState(null);
    const [newProject, setNewProject] = useState({
        title: '',
        description: '',
        startDate: '',
        endDate: '',
        responsible: '',
        imageUrl: '',
        tasks: []
    });
    const [imageFile, setImageFile] = useState(null);
    const storage = getStorage();
    const { currentUser, hasRole, getIdTokenResult } = useAuth();
    const isEditor = hasRole('editor');
    const isAdmin = hasRole('admin');
    const isSuperAdmin = hasRole('superAdmin');

    useEffect(() => {
        fetchProjects();
        checkApplicationStatus();
    }, [currentUser]);

    const fetchProjects = async () => {
        try {
            setLoading(true);
            setError(null);

            // Check cache first
            const cachedProjects = projectsCache.get('all');
            if (cachedProjects) {
                setProjects(cachedProjects);
                setLoading(false);
                return;
            }

            const response = await projectsAPI.getAllProjects();

            if (!response || !response.projects) {
                throw new Error('No projects data received');
            }

            const projectsArray = Array.isArray(response.projects) ? response.projects : [];
            
            projectsCache.set('all', projectsArray);
            setProjects(projectsArray);
        } catch (error) {
            setError(errorHandler.processError(error).message);
            setProjects([]); // Set empty array on error
        } finally {
            setLoading(false);
        }
    };

    const handleImageChange = (e) => {
        const file = e.target.files[0];
        if (!file) return;
      
        const allowedTypes = ['image/jpeg', 'image/png', 'image/webp'];
        const maxSizeMB = 5;
      
        if (!allowedTypes.includes(file.type)) {
          setError('Only JPG, PNG, or WebP images are allowed.');
          return;
        }
      
        if (file.size > maxSizeMB * 1024 * 1024) {
          setError(`Image must be smaller than ${maxSizeMB}MB.`);
          return;
        }
      
        setImageFile(file);
    };

    const uploadImage = async () => {
        if (!imageFile) return null;
        setLoading(true);
        try {
            const imagePath = `${STORAGE_PATHS.PROJECTS || 'projects'}/${uuidv4()}_${imageFile.name}`;
            const fileRef = storageRef(storage, imagePath);
            const uploadResult = await uploadBytes(fileRef, imageFile);
            const downloadURL = await getDownloadURL(uploadResult.ref);
            return downloadURL;
        } catch (error) {
            setError('Failed to upload project image: ' + error.message);
            throw error; // Re-throw to be caught by handleSubmit
        } finally {
            setLoading(false);
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);
        setError(null);

        try {
            // Validate dates
            if (new Date(newProject.startDate) >= new Date(newProject.endDate)) {
                throw new Error('End date must be after start date');
            }

            // Validate required fields
            if (!newProject.title || !newProject.description || !newProject.startDate || !newProject.endDate || !newProject.responsible) {
                throw new Error('Please fill in all required fields');
            }

            let projectDataToSend = { ...newProject };

            if (imageFile) {
                const uploadedImageUrl = await uploadImage();
                if (uploadedImageUrl) {
                    projectDataToSend.imageUrl = uploadedImageUrl;
                } else if (!editingProject || !editingProject.imageUrl) {
                    // If upload failed and it's a new project or an existing project without an image, clear imageUrl
                    projectDataToSend.imageUrl = ''; 
                }
            }
            
            // Remove imageFile from the data to be sent to the backend
            delete projectDataToSend.imageFile; 
            // Remove any local preview URL if it exists
            delete projectDataToSend.imagePreviewUrl; 

            if (editingProject) {
                await projectsAPI.updateProject(editingProject.id, projectDataToSend);
            } else {
                await projectsAPI.createProject(projectDataToSend);
            }

            // Clear form and refresh projects
            setNewProject({
                title: '',
                description: '',
                startDate: '',
                endDate: '',
                responsible: '',
                imageUrl: '', // Reset imageUrl
                tasks: []
            });
            setImageFile(null); // Reset image file state
            setShowAddForm(false);
            setEditingProject(null);
            projectsCache.clear();
            fetchProjects();
        } catch (error) {
            // setError is already handled by uploadImage if it fails there
            if (!error.message.includes('Failed to upload')) { 
                setError(errorHandler.processError(error).message);
            }
        } finally {
            setLoading(false); // Ensure loading is set to false
        }
    };

    const handleDelete = async (projectId) => {
        if (!isSuperAdmin) {
            setError('Only super admins can delete projects');
            return;
        }

        if (!window.confirm('Are you sure you want to delete this project?')) {
            return;
        }

        try {
            await projectsAPI.deleteProject(projectId);
            projectsCache.clear();
            fetchProjects();
        } catch (error) {
            setError(errorHandler.processError(error).message);
        }
    };

    const handleEdit = (projectId) => {
        const project = projects.find(p => p.id === projectId);
        if (project) {
            setEditingProject(project);
            setNewProject({
                ...project,
                startDate: project.startDate ? new Date(project.startDate).toISOString().split('T')[0] : '',
                endDate: project.endDate ? new Date(project.endDate).toISOString().split('T')[0] : ''
            });
            setShowAddForm(true);
        }
    };

    const checkApplicationStatus = async () => {
        if (!currentUser) return;
        try {
            const idTokenResult = await getIdTokenResult();
            if (idTokenResult) {
                setHasApplied(idTokenResult.claims.hasAppliedToAcademy || false);
            }
        } catch (error) {
        }
    };

    if (loading) return <LoadingSpinner />;
    if (error) return <ErrorDisplay message={error} />;

    if (!projects.length && !isAdmin) return (
        <div className="projects-container">
            <div className="page-header">
                <div className="page-header-content">
                    <h1>Our Projects</h1>
                    <p className="page-header-description">
                        Discover our initiatives and programs making a difference in the Mediterranean region.
                    </p>
                </div>
            </div>
            <div className="projects-empty">
                <i className="fas fa-folder-open" style={{ fontSize: '3rem', color: '#cbd5e1', marginBottom: '1rem' }}></i>
                <p>No projects found at the moment. Please check back later.</p>
            </div>
        </div>
    );

    return (
        <div className="projects-container">
            <div className="page-header">
                <div className="page-header-content">
                    <h1>Our Projects</h1>
                    <p className="page-header-description">
                        Discover our initiatives and programs making a difference in the Mediterranean region.
                    </p>
                    <div className="page-header-buttons">
                        <Link
                            to={currentUser ? "/academy" : "/sign-in"}
                            className="page-header-button"
                        >
                            <i className="fas fa-graduation-cap"></i>
                            {currentUser ? (hasApplied ? "View Academy Application Status" : "Apply to The Mediterranean Academy of Youth") : "Login to Apply to The Mediterranean Academy of Youth"}
                        </Link>
                        {(isAdmin || isEditor) && (
                            <button 
                                className="page-header-button"
                                onClick={() => setShowAddForm(true)}
                            >
                                <i className="fas fa-plus"></i>
                                Add New Project
                            </button>
                        )}
                    </div>
                </div>
            </div>

            {showAddForm && (
                <div className="project-form-container">
                    <div className="project-form">
                        <h2>{editingProject ? 'Edit Project' : 'Add New Project'}</h2>
                        <form onSubmit={handleSubmit}>
                            <div className="form-group">
                                <label htmlFor="title">Title *</label>
                                <input
                                    type="text"
                                    id="title"
                                    value={newProject.title}
                                    onChange={(e) => setNewProject({...newProject, title: e.target.value})}
                                    placeholder="Enter project title"
                                    required
                                />
                            </div>
                            <div className="form-group">
                                <label htmlFor="description">Description *</label>
                                <textarea
                                    id="description"
                                    value={newProject.description}
                                    onChange={(e) => setNewProject({...newProject, description: e.target.value})}
                                    placeholder="Enter project description"
                                    required
                                />
                            </div>
                            <div className="form-row">
                                <div className="form-group">
                                    <label htmlFor="startDate">Start Date *</label>
                                    <input
                                        type="date"
                                        id="startDate"
                                        value={newProject.startDate}
                                        onChange={(e) => setNewProject({...newProject, startDate: e.target.value})}
                                        required
                                    />
                                </div>
                                <div className="form-group">
                                    <label htmlFor="endDate">End Date</label>
                                    <input
                                        type="date"
                                        id="endDate"
                                        value={newProject.endDate}
                                        onChange={(e) => setNewProject({...newProject, endDate: e.target.value})}
                                    />
                                </div>
                            </div>
                            <div className="form-group">
                                <label htmlFor="responsible">Responsible Person *</label>
                                <input
                                    type="text"
                                    id="responsible"
                                    value={newProject.responsible}
                                    onChange={(e) => setNewProject({...newProject, responsible: e.target.value})}
                                    placeholder="Enter responsible person's name"
                                    required
                                />
                            </div>
                            <div className="form-group">
                                <label htmlFor="image">Project Image</label>
                                <input
                                    type="file"
                                    id="image"
                                    accept="image/*"
                                    onChange={handleImageChange}
                                />
                                {(newProject.imageUrl || (imageFile && URL.createObjectURL(imageFile))) && (
                                    <div className="image-preview" style={{ marginTop: '10px', maxWidth: '200px' }}>
                                        <img 
                                            src={imageFile ? URL.createObjectURL(imageFile) : newProject.imageUrl} 
                                            alt="Project preview" 
                                            style={{ width: '100%', borderRadius: '4px' }}
                                        />
                                    </div>
                                )}
                            </div>
                            <div className="form-actions">
                                <button 
                                    type="button" 
                                    className="cancel-button"
                                    onClick={() => {
                                        setShowAddForm(false);
                                        setEditingProject(null);
                                        setNewProject({
                                            title: '',
                                            description: '',
                                            startDate: '',
                                            endDate: '',
                                            responsible: '',
                                            imageUrl: '',
                                            tasks: []
                                        });
                                    }}
                                >
                                    Cancel
                                </button>
                                <button type="submit" className="submit-button">
                                    {editingProject ? 'Update Project' : 'Create Project'}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}

            <div className="projects-grid">
                {projects.map(project => (
                    <div key={project.id} className="project-card">
                        <div className="project-image-container">
                            <img src={project.imageUrl || '/images/default-project.png'} alt={project.title} />
                        </div>
                        <div className="project-content">
                            <h2>{project.title}</h2>
                            <p className="project-description">{project.description}</p>
                            <div className="project-details">
                                <p><strong>Responsible:</strong> {project.responsible || 'Not assigned'}</p>
                                <p><strong>Start Date:</strong> {project.startDate ? new Date(project.startDate).toLocaleDateString() : 'Not set'}</p>
                                <p><strong>End Date:</strong> {project.endDate ? new Date(project.endDate).toLocaleDateString() : 'Not set'}</p>
                                <p><strong>Created:</strong> {project.createdAt ? new Date(project.createdAt).toLocaleDateString() : 'Not set'}</p>
                                {project.tasks && project.tasks.length > 0 && (
                                    <p><strong>Tasks:</strong> {project.tasks.length}</p>
                                )}
                                {project.updatedAt && (
                                    <p><strong>Last Updated:</strong> {new Date(project.updatedAt).toLocaleDateString()}</p>
                                )}
                            </div>
                            
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                <Link to={`/projects/${project.id}`} className="project-view-link">
                                    <i className="fas fa-eye"></i> View Details
                                </Link>
                                
                                {(isAdmin || isEditor) && (
                                    <div className="project-actions">
                                        <button onClick={() => handleEdit(project.id)}>
                                            <i className="fas fa-edit"></i> Edit
                                        </button>
                                        {isSuperAdmin && (
                                            <button onClick={() => handleDelete(project.id)}>
                                                <i className="fas fa-trash-alt"></i> Delete
                                            </button>
                                        )}
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                ))}
            </div>
        </div>
    );
}

export default Projects; 